import { plural, t } from "@lingui/macro";
import React from "react";

import {
  Box,
  HStack,
  VStack,
  Pressable,
} from "@whoosh/design-system/src/components/primitive";

import { PlayerDetailBanner } from "./components/PlayerDetailBanner";
import { QueueSlotFooter } from "./components/QueueSlotFooter";
import { QueueSlotHeader } from "./components/QueueSlotHeader";
import { TimeSlotHeader } from "./components/TimeSlotHeader";

export type BorderRadiusOption =
  | "0"
  | "0.5"
  | "1"
  | "2"
  | "4"
  | "6"
  | "8"
  | "full";

type PrivateTimeSlotCardProps = {
  isUnavailable?: boolean;
  time?: string;
  availableSpots?: number;
  isRequestAvailable?: boolean;
  ctaText?: string;
  players: { fullName: string; icons?: React.JSX.Element; isGuest?: boolean }[];
  onPress?: () => void;
  borderRadius?: {
    top?: BorderRadiusOption;
    bottom?: BorderRadiusOption;
  };
  shouldShowCheckbox?: boolean;
  checkboxIsChecked?: boolean;
  canUpdate: boolean;
  location?: string;
  isQueueSlot?: boolean;
  currentUserIsInBooking?: boolean;
  restrictionList?: React.JSX.Element;
};

export const PrivateTimeSlotCard = ({
  isUnavailable,
  time,
  availableSpots,
  isRequestAvailable,
  ctaText,
  players,
  onPress,
  borderRadius,
  shouldShowCheckbox,
  checkboxIsChecked,
  canUpdate,
  location,
  isQueueSlot,
  currentUserIsInBooking,
  restrictionList,
}: PrivateTimeSlotCardProps) => {
  const slotsAreUnavailable =
    !canUpdate && (isUnavailable || (availableSpots || 0) <= 0);
  const hasAvailableSlots = !!availableSpots && availableSpots > 0;

  const backgroundColor =
    (slotsAreUnavailable || !canUpdate) && !isQueueSlot
      ? "grayscale-100"
      : "surface-primary";

  const headingTextColor = slotsAreUnavailable
    ? "text-secondary"
    : "text-primary";

  const badgeText =
    isQueueSlot && !canUpdate
      ? undefined
      : isUnavailable
      ? t`Unavailable`
      : isRequestAvailable
      ? t`Request`
      : availableSpots !== undefined && availableSpots <= 0
      ? t`Full`
      : availableSpots === undefined
      ? undefined
      : plural(availableSpots, {
          one: "# Free",
          other: "# Free",
        });

  const getBadgeBackground = () => {
    if (slotsAreUnavailable || isUnavailable) return "grayscale-200";
    if (isRequestAvailable) return "surface-accent-blue-subtlest";
    if (hasAvailableSlots) return "surface-main-primary-subtlest";
    return "surface-main-primary-subtlest";
  };

  const getBadgeTextColor = () => {
    if (slotsAreUnavailable || isUnavailable) return "text-disabled";
    if (isRequestAvailable) return "color-accent-blue-500";
    if (!!availableSpots && availableSpots > 0) return "main-primary-800";
    return "main-primary-800";
  };

  const borderColor =
    currentUserIsInBooking && canUpdate
      ? "surface-main-primary-neutral"
      : slotsAreUnavailable || !canUpdate
      ? "grayscale-400"
      : "transparent";

  const shouldShowShadow = !isQueueSlot && !isUnavailable && canUpdate;

  const shadowStyle = shouldShowShadow
    ? {
        shadowColor: "#000000",
        shadowOffset: { width: 0, height: 2 },
        shadowRadius: 6,
        shadowOpacity: 0.04,
        elevation: 2,
      }
    : undefined;

  return (
    <Pressable
      borderColor={borderColor}
      backgroundColor={backgroundColor}
      p={1}
      borderWidth={2}
      onPress={onPress}
      disabled={onPress === undefined}
      aria-label={t`time-slot-card`}
      borderBottomLeftRadius={borderRadius?.bottom}
      borderBottomRightRadius={borderRadius?.bottom}
      borderTopLeftRadius={borderRadius?.top}
      borderTopRightRadius={borderRadius?.top}
      style={shadowStyle}
      _pressed={{
        backgroundColor: onPress ? "grayscale-100" : backgroundColor,
      }}
      testID={`time-slot-card-${location}-${time}`}
    >
      <VStack>
        {restrictionList ? restrictionList : null}
        <Box p={3}>
          {isQueueSlot ? (
            <QueueSlotHeader time={time} />
          ) : (
            <TimeSlotHeader
              time={time}
              badgeText={badgeText}
              ctaText={ctaText}
              badgeBackground={getBadgeBackground()}
              badgeTextColor={getBadgeTextColor()}
              headingTextColor={headingTextColor}
              shouldShowCheckbox={shouldShowCheckbox}
              checkboxIsChecked={checkboxIsChecked}
              location={location}
            />
          )}
          <HStack
            flex={1}
            flexWrap="wrap"
            alignItems="center"
            pt={players.length && !!time ? 2 : 1}
          >
            {players.map((player, i) => (
              <PlayerDetailBanner
                key={`${i}-${player.fullName}`}
                fullName={player.fullName}
                icons={player.icons}
                isGuest={player.isGuest}
              />
            ))}
          </HStack>
          {isQueueSlot ? (
            <QueueSlotFooter
              hasAvailableSlots={hasAvailableSlots}
              badgeText={badgeText}
              ctaText={ctaText}
              badgeBackground={getBadgeBackground()}
              badgeTextColor={getBadgeTextColor()}
            />
          ) : null}
        </Box>
      </VStack>
    </Pressable>
  );
};
